import { PutObjectCommand } from '@aws-sdk/client-s3';
import {
  FormEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  getPurchasePricesCount,
  getPurchasePricesUploadedFiles,
} from '../core/api';
import { sleep } from '../core/helpers';
import { getS3Client, PURCHASE_PRICES_BUCKET_NAME } from '../core/s3';
import {
  addNotification,
  removeNotification,
} from '../core/store/notificationsSlice';
import {
  useAppDispatch,
  useAppSelector,
  usePolling,
  useQueriesTyped,
} from '../hooks';
import {
  FileStatusEnum,
  PurchasePricesUploadedFile,
} from '../pages/PurchasePrices/PurchasePricesUploader';
import * as Sentry from '@sentry/browser';
import { ACCEPTED_EXTS } from './useManualUpload';

const processingPricesNotificationId = 'processing-pp';

export const usePurchasePrices = () => {
  const { isPollingActive, deactivatePolling, activatePolling } = usePolling();
  const controller = useRef(new AbortController());
  const { t } = useTranslation();
  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany?.company_id
  );
  const user = useAppSelector((s) => s.user.user);
  const dispatch = useAppDispatch();

  const [isUploading, setIsUploading] = useState<{
    [key: string]: boolean;
  }>({});
  const [filesToUpload, setFilesToUpload] = useState<{
    [key: string]: File | undefined;
  }>({});
  const [uploadedFile, setUploadedFile] =
    useState<PurchasePricesUploadedFile>();

  const queryClient = useQueryClient();

  const [count, uploadedFiles] = useQueriesTyped([
    {
      queryKey: ['SellerPurchasePricesCount', user!.Id, activeCompanyId],
      queryFn: () =>
        getPurchasePricesCount(activeCompanyId!, controller.current),
      retry: false,
    },
    {
      queryKey: ['SellerPurchasePricesFiles', user!.Id, activeCompanyId],
      queryFn: () =>
        getPurchasePricesUploadedFiles(activeCompanyId!, controller.current),
      retry: false,
    },
  ]);

  const isLoading = count.isLoading || uploadedFiles.isLoading;

  const filesData = uploadedFiles.data;

  const onFinishPollingFiles = useCallback(
    async (purchasePricesFiles: PurchasePricesUploadedFile[]) => {
      getPurchasePricesCount(activeCompanyId!, controller.current)
        .then((countRes) => {
          deactivatePolling(activeCompanyId!);
          queryClient.setQueryData(
            ['SellerPurchasePricesCount', user!.Id, activeCompanyId],
            countRes
          );
          queryClient.setQueryData(
            ['SellerPurchasePricesFiles', user!.Id, activeCompanyId],
            purchasePricesFiles
          );
          setUploadedFile(undefined);

          dispatch(removeNotification(processingPricesNotificationId));

          if (!!purchasePricesFiles.length && countRes) {
            dispatch(
              addNotification({
                type: 'error',
                text: t('*count* purchase prices still missing').replace(
                  '*count*',
                  String(countRes)
                ),
                interval: 10000,
              })
            );
          }
        })
        .catch(console.error);
    },
    // eslint-disable-next-line
    [activeCompanyId, queryClient, user, dispatch]
  );

  useEffect(() => {
    if (
      filesData !== undefined &&
      !!filesData.length &&
      filesData.some((f) => f.status === 0) &&
      !isPollingActive(activeCompanyId!)
    ) {
      activatePolling(activeCompanyId!);
      pollFiles(activeCompanyId!, controller.current, onFinishPollingFiles);
    }
    // eslint-disable-next-line
  }, [activeCompanyId, filesData, onFinishPollingFiles]);

  useEffect(() => {
    const abortController = controller.current;
    return () => {
      abortController.abort();
    };
  }, []);

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!filesToUpload[activeCompanyId!]) return;
    setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: true }));
    try {
      const s3Client = await getS3Client();
      await s3Client.send(
        new PutObjectCommand({
          Bucket: PURCHASE_PRICES_BUCKET_NAME,
          Key: `${activeCompanyId}/${filesToUpload[activeCompanyId!]?.name}`,
          Body: filesToUpload[activeCompanyId!],
          Metadata: {
            userId: user!.Id,
          },
        })
      );
      s3Client.destroy();

      setUploadedFile({
        bucket: '',
        file: '/' + filesToUpload[activeCompanyId!]?.name,
        id: 0,
        imported_at: '',
        rows_number: 0,
        size: 0,
        status: 0,
        updated_products_number: 0,
        validation_errors: [],
      });

      dispatch(
        addNotification({
          type: 'success',
          text: t('Data file sent'),
        })
      );
      setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: false }));

      dispatch(
        addNotification({
          type: 'loading',
          text: t('Processing purchase price file'),
          state: 'open',
          id: processingPricesNotificationId,
        })
      );
      setTimeout(async () => {
        try {
          const files = await getPurchasePricesUploadedFiles(
            activeCompanyId!,
            controller.current
          );

          if (files.some((f) => f.status === 0)) {
            queryClient.setQueryData(
              ['SellerPurchasePricesFiles', user!.Id, activeCompanyId],
              files
            );

            setUploadedFile(undefined);
          } else {
            onFinishPollingFiles(files);
          }
        } catch (error) {
          console.error(error);
        }
      }, 5000);
      deleteFileToUpload();
    } catch (error) {
      if ((error as any).message) {
        Sentry.captureMessage((error as any).message);
      } else if ((error as any).name) {
        Sentry.captureMessage((error as any).name);
      } else {
        Sentry.captureException(error);
      }
      dispatch(
        addNotification({
          text: t('Error has occurred while uploading file *filename*').replace(
            '*filename*',
            filesToUpload?.[activeCompanyId!]?.name || ''
          ),
          type: 'error',
        })
      );

      setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: false }));
    }
  };

  const onSubmitPrevPeriod: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!filesToUpload[activeCompanyId!]) return;
    setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: true }));

    try {
      const s3Client = await getS3Client();
      await s3Client.send(
        new PutObjectCommand({
          Bucket: PURCHASE_PRICES_BUCKET_NAME,
          Key: `${activeCompanyId}/${filesToUpload[activeCompanyId!]?.name}`,
          Body: filesToUpload[activeCompanyId!],
          Metadata: {
            userId: user!.Id,
          },
        })
      );
      s3Client.destroy();
      dispatch(
        addNotification({
          type: 'success',
          text: t('Data file sent'),
        })
      );
      deleteFileToUpload();
    } catch (error) {
      if ((error as any).message) {
        Sentry.captureMessage((error as any).message);
      } else if ((error as any).name) {
        Sentry.captureMessage((error as any).name);
      } else {
        Sentry.captureException(error);
      }
      dispatch(
        addNotification({
          text: t('Error has occurred while uploading file *filename*').replace(
            '*filename*',
            filesToUpload?.[activeCompanyId!]?.name || ''
          ),
          type: 'error',
        })
      );
    }
    setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: false }));
  };

  const deleteFileToUpload = () => {
    setFilesToUpload((prev) => ({ ...prev, [activeCompanyId!]: undefined }));
  };

  const onSelect = (files: File[] | null) => {
    const filesForUploading = files?.filter((f) => {
      const parts = f.name.split('.');
      const ext = parts[parts.length - 1];
      return ACCEPTED_EXTS.includes((ext || '').toLowerCase());
    });

    if (!files || !filesForUploading) {
      return;
    }

    if (files.length > filesForUploading.length) {
      dispatch(
        addNotification({
          text: t('You can upload CSV, XLS, XLSX file.'),
          type: 'error',
        })
      );
      return;
    }

    const f = files[0];
    const blob = f.slice(0, f.size, f.type);
    const nameParts = f.name.split('.');
    setFilesToUpload((prev) => ({
      ...prev,
      [activeCompanyId!]: new File(
        [blob],
        `${nameParts.slice(0, -1).join('.')}_${Date.now()}.${
          nameParts[nameParts.length - 1]
        }`,
        { type: f.type }
      ),
    }));
  };

  return {
    uploadedFiles: [
      ...(uploadedFiles.data || []),
      ...(uploadedFile ? [uploadedFile] : []),
    ],
    onSubmit,
    isUploading: isUploading[activeCompanyId!] || false,
    fileToUpload: filesToUpload?.[activeCompanyId!],
    onSelect,
    count: count.data,
    isLoading,
    deleteFileToUpload,
    onSubmitPrevPeriod,
  };
};

const pollFiles = async (
  company_id: string,
  controller: AbortController,
  cb: (res: PurchasePricesUploadedFile[]) => void,
  count: number = 1
) => {
  try {
    const res = await getPurchasePricesUploadedFiles(company_id, controller);

    if (res.some((f) => f.status === FileStatusEnum.CREATED)) {
      await sleep(5000);
      pollFiles(company_id, controller, cb);
    } else {
      cb(res);
    }
  } catch {
    if (count < 3) {
      await sleep(5000);
      try {
        pollFiles(company_id, controller, cb, ++count);
      } catch {}
    }
  }
};
